/**
 * PolishSynthesisDisplay — Shows polished lyrics with compare view and section decisions.
 */

import React, { useState } from 'react';
import { Sparkles, Copy, Check, ChevronDown, Columns } from 'lucide-react';
import { Badge, Button } from '@/components/ui';
import { LYRIC_WRITER_AGENTS } from '@/data/lyricOrchestratorAgents';
import { copyToClipboard } from '@/utils/clipboard';
import type { PolishSynthesis } from '@/types/lyricOrchestrator.types';

interface PolishSynthesisDisplayProps {
  synthesis: PolishSynthesis;
  originalLyrics: string;
  onAccept: () => void;
}

const changeTypeConfig = {
  keep: { variant: 'success' as const, label: 'Kept' },
  tweak: { variant: 'warning' as const, label: 'Tweaked' },
  rewrite: { variant: 'danger' as const, label: 'Rewritten' },
};

export const PolishSynthesisDisplay: React.FC<PolishSynthesisDisplayProps> = ({
  synthesis,
  originalLyrics,
  onAccept,
}) => {
  const [copied, setCopied] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showRationale, setShowRationale] = useState(false);

  const handleCopy = async () => {
    try {
      await copyToClipboard(synthesis.polishedLyrics);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const agentMap = new Map(LYRIC_WRITER_AGENTS.map(a => [a.id, a]));

  // Strip physics blocks from original for clean compare
  const cleanOriginal = originalLyrics
    .replace(/\{LAYER-1 VOCAL PHYSICS\}\n(?:\[.*\]\n?)*/g, '')
    .replace(/\{SCREAM-PHYSICS:.*?\}\n(?:\[.*\]\n?)*/g, '')
    .replace(/\{DEEP-DIVE VOCAL LAYERS\}\n(?:(?:\{(?:DIALECT|AGING|BREATH|ARC)-PHYSICS:.*?\}\n\[.*\]\n?)*)/g, '')
    .replace(/\{NARRATIVE-ENGINE\}[\s\S]*?\{\/NARRATIVE-ENGINE\}/g, '')
    .trim();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-amber-400" />
          <h3 className="text-sm font-black text-slate-200 uppercase tracking-wider">
            Parliament Polish — Polished Lyrics
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCompare(!showCompare)}
            leftIcon={<Columns size={14} />}
            className={showCompare ? 'text-amber-400 border-amber-500/50' : 'text-slate-400'}
          >
            {showCompare ? 'Polished' : 'Compare'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            leftIcon={copied ? <Check size={14} /> : <Copy size={14} />}
            className="text-slate-400"
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onAccept}
          >
            Accept Polished Lyrics
          </Button>
        </div>
      </div>

      {/* Title + Summary */}
      <div className="bg-slate-900/60 rounded-lg p-4 border border-slate-800">
        <h4 className="text-lg font-black text-amber-400">{synthesis.title}</h4>
        {synthesis.summary && (
          <p className="text-[12px] text-slate-400 mt-1 italic">{synthesis.summary}</p>
        )}
        {synthesis.styleTags && (
          <div className="flex flex-wrap gap-1 mt-2">
            {synthesis.styleTags.split(',').map((tag, i) => (
              <Badge key={i} variant="default-muted" size="xs">{tag.trim()}</Badge>
            ))}
          </div>
        )}
      </div>

      {/* Lyrics — Compare or Polished view */}
      {showCompare ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-950/50 rounded-lg p-4 border border-slate-800">
            <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Original</h5>
            <pre className="text-[11px] text-slate-400 whitespace-pre-wrap font-sans leading-relaxed">
              {cleanOriginal}
            </pre>
          </div>
          <div className="bg-slate-950/50 rounded-lg p-4 border border-green-900/30">
            <h5 className="text-[10px] font-black text-green-400 uppercase tracking-widest mb-2">Polished</h5>
            <pre className="text-[12px] text-slate-200 whitespace-pre-wrap font-sans leading-relaxed">
              {synthesis.polishedLyrics}
            </pre>
          </div>
        </div>
      ) : (
        <div className="bg-slate-950/50 rounded-lg p-4 border border-slate-800">
          <pre className="text-[12px] text-slate-200 whitespace-pre-wrap font-sans leading-relaxed">
            {synthesis.polishedLyrics}
          </pre>
        </div>
      )}

      {/* Section Decisions */}
      {synthesis.sectionDecisions.length > 0 && (
        <div>
          <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
            Section Decisions
          </h5>
          <div className="flex flex-wrap gap-2">
            {synthesis.sectionDecisions.map((decision, i) => {
              const agent = agentMap.get(decision.sourceAgentId);
              const colorClass = agent?.color || 'text-slate-400';
              const ctConfig = changeTypeConfig[decision.changeType];

              return (
                <div
                  key={i}
                  className="flex items-center gap-1.5 bg-slate-900/60 rounded-md px-2 py-1 border border-slate-800"
                >
                  <span className="text-[10px] font-bold text-slate-300">{decision.section}</span>
                  <span className="text-slate-700">&larr;</span>
                  <span className={`text-[10px] font-bold ${colorClass}`}>
                    {agent?.name || decision.sourceAgentId}
                  </span>
                  <Badge variant={ctConfig.variant} size="xs">{ctConfig.label}</Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Overall Improvement */}
      {synthesis.overallImprovement && (
        <div className="border-l-4 border-amber-500/50 pl-3 py-2">
          <p className="text-[12px] text-slate-300 italic">{synthesis.overallImprovement}</p>
        </div>
      )}

      {/* Section Rationale (collapsible) */}
      {synthesis.sectionDecisions.some(d => d.rationale) && (
        <div>
          <button
            onClick={() => setShowRationale(!showRationale)}
            className="flex items-center gap-1 text-[10px] font-black text-slate-500 uppercase tracking-widest hover:text-slate-400 transition-colors"
          >
            <ChevronDown
              size={12}
              className={`transition-transform duration-200 ${showRationale ? 'rotate-180' : ''}`}
            />
            Decision Rationale
          </button>
          {showRationale && (
            <div className="mt-2 space-y-1 animate-in slide-in-from-top-2 duration-200">
              {synthesis.sectionDecisions.filter(d => d.rationale).map((d, i) => (
                <p key={i} className="text-[11px] text-slate-400 bg-slate-900/40 rounded p-2">
                  <span className="font-bold text-slate-300">{d.section}:</span> {d.rationale}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
